import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import client from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import ProcedureFieldKey from '../../components/ProcedureFieldKey';
import { FieldHeader } from '../../utils/procedureFieldDefs';
import '../../components/ProcedureFieldKey.css';
import './AdminPages.css';
import './ProcedureDatabase.css';

// Column shape used by the "Combined DB" tab of the source workbook
// (cardiovascular_procedure_roi_database_with_open_aaa_repair.xlsx). Keep
// this list in lock-step with the Pick(...) aliases in
// backend/Controllers/ProceduresController.cs so a CSV exported from the
// workbook imports as-is.
const CSV_COLUMNS = [
  { header: 'Service Line',                       field: 'serviceLine' },
  { header: 'Procedure',                          field: 'procedureName' },
  { header: 'Matching Key',                       field: 'matchingKey' },
  { header: 'CPT Code',                           field: 'cptCodes' },
  { header: 'MS-DRG/APC',                         field: 'msDrgApc' },
  { header: 'Avg Medicare Reimbursement',         field: 'avgMedicareReimbursement' },
  { header: 'Avg Medicare Margin %',              field: 'avgMedicareMarginPct' },
  { header: 'Avg Medicare CM/Case',               field: 'avgMedicareProfitPerCase' },
  { header: 'Avg Medicare Direct LTV',            field: 'avgMedicareDirectLtv' },
  { header: 'Avg Medicare Halo',                  field: 'avgMedicareHalo' },
  { header: 'Avg Medicare Total Value',           field: 'avgMedicareTotalValue' },
  { header: 'Medicare Volume Category',           field: 'medicareVolumeCategory' },
  { header: 'Private-to-Medicare Ratio',          field: 'privateToMedicareRatio' },
  { header: 'Avg Private Reimbursement',          field: 'avgPrivateReimbursement' },
  { header: 'Avg Private Margin %',               field: 'avgPrivateMarginPct' },
  { header: 'Avg Private CM/Case',                field: 'avgPrivateProfitPerCase' },
  { header: 'Avg Private Direct LTV',             field: 'avgPrivateDirectLtv' },
  { header: 'Avg Private Halo',                   field: 'avgPrivateHalo' },
  { header: 'Avg Private Total Value',            field: 'avgPrivateTotalValue' },
  { header: 'Private Volume Category',            field: 'privateVolumeCategory' },
  { header: 'Data Basis/Notes',                   field: 'dataBasisNotes' },
  { header: 'Source URLs',                        field: 'sourceUrls' },
];

const EMPTY_DRAFT = {
  serviceLine: '',
  procedureName: '',
  matchingKey: '',
  cptCodes: '',
  msDrgApc: '',
  avgMedicareReimbursement: '',
  avgPrivateReimbursement: '',
};

const formatMoney = (n) =>
  (typeof n === 'number' && !Number.isNaN(n))
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : '—';

const formatPct = (n) =>
  (typeof n === 'number' && !Number.isNaN(n))
    ? `${(n * 100).toFixed(1)}%`
    : '—';

const formatRatio = (n) =>
  (typeof n === 'number' && !Number.isNaN(n))
    ? `${n.toFixed(2)}x`
    : '—';

const numberOrNull = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
};

const TABS = [
  { id: 'procedures',  label: 'Procedures' },
  { id: 'methodology', label: 'Methodology & Assumptions' },
];

function ProcedureDatabase() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('admin.procedures', true);

  const [activeTab, setActiveTab] = useState('procedures');
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newDraft, setNewDraft] = useState(EMPTY_DRAFT);
  const [search, setSearch] = useState('');
  const [serviceLineFilter, setServiceLineFilter] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/procedures');
      setProcedures(res.data?.procedures || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load procedures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const serviceLines = useMemo(() => {
    const set = new Set();
    procedures.forEach(p => p.serviceLine && set.add(p.serviceLine));
    return Array.from(set).sort();
  }, [procedures]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return procedures.filter(p => {
      if (serviceLineFilter && p.serviceLine !== serviceLineFilter) return false;
      if (!q) return true;
      const blob = [
        p.serviceLine, p.procedureName, p.matchingKey, p.cptCodes, p.msDrgApc,
        p.medicareVolumeCategory, p.privateVolumeCategory, p.dataBasisNotes,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [procedures, search, serviceLineFilter]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditingDraft({ ...p });
    setIsAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft({});
  };

  const saveEdit = async () => {
    try {
      const res = await client.put(`/api/procedures/${editingId}`, toApiPayload(editingDraft));
      const saved = res.data?.procedure;
      setProcedures(prev => prev.map(p => p.id === editingId ? saved : p));
      cancelEdit();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save procedure');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this procedure?')) return;
    try {
      await client.delete(`/api/procedures/${id}`);
      setProcedures(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete procedure');
    }
  };

  const handleAdd = async () => {
    if (!newDraft.serviceLine.trim() || !newDraft.procedureName.trim()) {
      setError('Service line and procedure name are required');
      return;
    }
    try {
      const res = await client.post('/api/procedures', toApiPayload(newDraft));
      const created = res.data?.procedure;
      setProcedures(prev => [...prev, created].sort(compareByServiceThenName));
      setIsAdding(false);
      setNewDraft(EMPTY_DRAFT);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add procedure');
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-importing the same file
    if (!file) return;

    setImportStatus('Parsing CSV…');
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          setImportStatus(`Importing ${results.data.length} procedures…`);
          const res = await client.post('/api/procedures/import', { procedures: results.data });
          setImportStatus(res.data?.message || `Imported ${res.data?.count ?? 0} procedures`);
          await load();
          setTimeout(() => setImportStatus(''), 3000);
        } catch (e) {
          setImportStatus('');
          setError(e.response?.data?.error || 'Failed to import procedures');
        }
      },
      error: () => {
        setImportStatus('');
        setError('Could not parse the CSV file');
      },
    });
  };

  const handleExport = () => {
    const rows = procedures.map(p => {
      const out = {};
      CSV_COLUMNS.forEach(c => { out[c.header] = p[c.field] ?? ''; });
      return out;
    });
    const csv = Papa.unparse(rows, { columns: CSV_COLUMNS.map(c => c.header) });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `procedures_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading procedures…</div>
      </div>
    );
  }

  return (
    <div className="admin-page procedure-db-page">
      <div className="admin-page-header">
        <div>
          <h1>Procedure Database</h1>
          <p className="admin-page-subtitle">
            Payer-aware catalog of clinical procedures (Medicare and Private)
            used by the Collaboration Analytics ROI tab. Bulk-import from a CSV
            export of the "Combined DB" tab in the source workbook, or edit
            inline.
          </p>
        </div>
        {activeTab === 'procedures' && (
          <div className="procedure-db-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {canEdit && (
              <button className="btn btn-blue" onClick={handleImportClick} disabled={!!importStatus}>
                ⬆ Import CSV
              </button>
            )}
            <button className="btn btn-green" onClick={handleExport} disabled={procedures.length === 0}>
              ⬇ Export CSV
            </button>
            {canEdit && (
              <button
                className="btn btn-purple"
                onClick={() => { setIsAdding(true); setEditingId(null); setNewDraft(EMPTY_DRAFT); }}
              >
                + Add Procedure
              </button>
            )}
          </div>
        )}
      </div>

      <div className="procedure-db-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeTab === t.id}
            className={`procedure-db-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {importStatus && <div className="procedure-db-status">{importStatus}</div>}
      {error && (
        <div className="procedure-db-error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      {activeTab === 'procedures' ? (
        <ProceduresTab
          procedures={procedures}
          filtered={filtered}
          serviceLines={serviceLines}
          search={search}
          setSearch={setSearch}
          serviceLineFilter={serviceLineFilter}
          setServiceLineFilter={setServiceLineFilter}
          isAdding={isAdding}
          newDraft={newDraft}
          setNewDraft={setNewDraft}
          handleAdd={handleAdd}
          cancelAdd={() => { setIsAdding(false); setNewDraft(EMPTY_DRAFT); setError(''); }}
          editingId={editingId}
          editingDraft={editingDraft}
          setEditingDraft={setEditingDraft}
          saveEdit={saveEdit}
          cancelEdit={cancelEdit}
          startEdit={startEdit}
          handleDelete={handleDelete}
          canEdit={canEdit}
        />
      ) : (
        <MethodologyTab />
      )}
    </div>
  );
}

function ProceduresTab({
  procedures, filtered, serviceLines,
  search, setSearch, serviceLineFilter, setServiceLineFilter,
  isAdding, newDraft, setNewDraft, handleAdd, cancelAdd,
  editingId, editingDraft, setEditingDraft, saveEdit, cancelEdit, startEdit, handleDelete,
  canEdit,
}) {
  // Synced top + bottom horizontal scrollbars so the user can scroll the
  // wide Medicare/Private grid without scrolling all the way to the
  // bottom. Same pattern as the Raw tab in CollaborationAnalytics, but
  // measures the table's real scrollWidth instead of using a per-column
  // hardcoded width — the procedure grid mixes wide text columns
  // (Service Line, Procedure, Matching Key) with narrow numeric ones.
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableInnerWidth, setTableInnerWidth] = useState(0);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => setTableInnerWidth(el.scrollWidth);
    measure();
    // Re-measure when the table re-renders (filter changed, edit row
    // toggled, etc.) by observing its size.
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      // Observe the inner table too because the wrap's own size may not
      // change even when content does (e.g. column widths shift when an
      // edit row's number inputs render).
      const inner = el.querySelector('table');
      if (inner) ro.observe(inner);
      return () => ro.disconnect();
    }
    return undefined;
  }, [filtered, isAdding, editingId]);

  const handleScroll = (which) => (e) => {
    const peer = which === 'top' ? tableScrollRef.current : topScrollRef.current;
    if (peer && peer.scrollLeft !== e.target.scrollLeft) {
      peer.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <>
      <div className="procedure-db-toolbar">
        <input
          className="procedure-db-search"
          type="search"
          placeholder="Search procedure, key, CPT, DRG…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="procedure-db-filter"
          value={serviceLineFilter}
          onChange={e => setServiceLineFilter(e.target.value)}
        >
          <option value="">All service lines</option>
          {serviceLines.map(sl => <option key={sl} value={sl}>{sl}</option>)}
        </select>
        <span className="procedure-db-count">
          {filtered.length === procedures.length
            ? `${procedures.length} procedure${procedures.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${procedures.length}`}
        </span>
      </div>

      {isAdding && (
        <AddProcedureForm
          draft={newDraft}
          onChange={setNewDraft}
          onSave={handleAdd}
          onCancel={cancelAdd}
        />
      )}

      <ProcedureFieldKey />

      <div
        ref={topScrollRef}
        onScroll={handleScroll('top')}
        className="procedure-db-scroll-top"
        aria-hidden="true"
      >
        <div style={{ width: tableInnerWidth || 1, height: 1 }} />
      </div>

      <div
        ref={tableScrollRef}
        onScroll={handleScroll('bottom')}
        className="procedure-db-table-wrap"
      >
        <table className="procedure-db-table">
          <thead>
            <tr className="procedure-db-group-row">
              <th colSpan={5} />
              <th colSpan={6} className="procedure-db-group procedure-db-group-medicare">Medicare</th>
              <th colSpan={7} className="procedure-db-group procedure-db-group-private">Private</th>
              {canEdit && <th />}
            </tr>
            <tr>
              <th>Service Line</th>
              <th>Procedure</th>
              <th>Matching Key</th>
              <th>CPT</th>
              <th>MS-DRG/APC</th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="reimb" /></th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="marginPct" /></th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="cm" /></th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="directLtv" /></th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="halo" /></th>
              <th className="num procedure-db-cell-medicare"><FieldHeader fieldId="total" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="pmRatio" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="reimb" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="marginPct" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="cm" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="directLtv" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="halo" /></th>
              <th className="num procedure-db-cell-private"><FieldHeader fieldId="total" /></th>
              {canEdit && <th className="actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 19 : 18} className="procedure-db-empty">
                  {procedures.length === 0
                    ? 'No procedures in database. Import a CSV file to get started.'
                    : 'No procedures match your filters.'}
                </td>
              </tr>
            )}
            {filtered.map(p => editingId === p.id ? (
              <EditRow
                key={p.id}
                draft={editingDraft}
                onChange={setEditingDraft}
                onSave={saveEdit}
                onCancel={cancelEdit}
                canEdit={canEdit}
              />
            ) : (
              <tr key={p.id}>
                <td>{p.serviceLine}</td>
                <td>{p.procedureName}</td>
                <td className="small">{p.matchingKey || '—'}</td>
                <td className="small">{p.cptCodes || '—'}</td>
                <td className="small">{p.msDrgApc || '—'}</td>
                <td className="num procedure-db-cell-medicare">{formatMoney(p.avgMedicareReimbursement)}</td>
                <td className="num procedure-db-cell-medicare">{formatPct(p.avgMedicareMarginPct)}</td>
                <td className="num procedure-db-cell-medicare">{formatMoney(p.avgMedicareProfitPerCase)}</td>
                <td className="num procedure-db-cell-medicare">{formatMoney(p.avgMedicareDirectLtv)}</td>
                <td className="num procedure-db-cell-medicare">{formatMoney(p.avgMedicareHalo)}</td>
                <td className="num procedure-db-cell-medicare bold">{formatMoney(p.avgMedicareTotalValue)}</td>
                <td className="num procedure-db-cell-private">{formatRatio(p.privateToMedicareRatio)}</td>
                <td className="num procedure-db-cell-private">{formatMoney(p.avgPrivateReimbursement)}</td>
                <td className="num procedure-db-cell-private">{formatPct(p.avgPrivateMarginPct)}</td>
                <td className="num procedure-db-cell-private">{formatMoney(p.avgPrivateProfitPerCase)}</td>
                <td className="num procedure-db-cell-private">{formatMoney(p.avgPrivateDirectLtv)}</td>
                <td className="num procedure-db-cell-private">{formatMoney(p.avgPrivateHalo)}</td>
                <td className="num procedure-db-cell-private bold">{formatMoney(p.avgPrivateTotalValue)}</td>
                {canEdit && (
                  <td className="actions">
                    <button className="icon-btn edit" onClick={() => startEdit(p)} title="Edit">✎</button>
                    <button className="icon-btn delete" onClick={() => handleDelete(p.id)} title="Delete">🗑</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MethodologyTab() {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/docs/procedures-methodology.md')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => { if (!cancelled) { setMarkdown(text); setLoading(false); } })
      .catch(err => { if (!cancelled) { setLoadError(String(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="procedure-db-methodology">
      <section className="procedure-db-methodology-summary">
        <h2>How the ROI dollars are computed</h2>
        <p>
          Each referral whose <code>referral_for</code> text matches a row in
          this database contributes the row's published per-payer dollar
          values to the Collaboration Analytics ROI tab. The matcher splits
          the <em>Matching Key</em> column on commas and substring-tests each
          phrase, then falls back to procedure name and CPT code.
        </p>

        <ProcedureFieldKey className="procedure-db-methodology-key" />

        <h3>Per-payer formulas</h3>
        <ul className="procedure-db-formula-list">
          <li><strong>Reimbursement</strong> — published national hospital facility benchmark (Medicare from CMS IPPS / OPPS / APC files; Private modeled from procedure-specific commercial-to-Medicare multipliers).</li>
          <li><strong>Margin %</strong> — contribution margin percentage: (Reimbursement − direct cost) ÷ Reimbursement. Direct costs are variable case costs, not full overhead.</li>
          <li><strong>CM / Case</strong> — contribution margin per index procedure (reimbursement × margin %, or anchored to published Medicare CM literature). Not operating profit.</li>
          <li><strong>Direct LTV</strong> — modeled downstream same-pathway contribution margin over ~24 months after the index case (excludes the index CM).</li>
          <li><strong>Halo</strong> — modeled adjacent-service-line contribution margin attributable to retaining the patient inside the cardiovascular ecosystem.</li>
          <li><strong>Total Value</strong> — CM/Case + Direct LTV + Halo. Stored as an authoritative number, not a derived sum.</li>
          <li><strong>Private-to-Medicare Ratio</strong> — Avg Private Reimbursement ÷ Avg Medicare Reimbursement.</li>
        </ul>

        <h3>The same-direct-cost assumption</h3>
        <p>
          Private CM is not Medicare CM times the payer ratio. The model
          assumes the hospital's <em>direct cost</em> is approximately the
          same regardless of payer, so the private uplift drops mostly to
          contribution margin after covering that cost:
        </p>
        <pre className="procedure-db-formula-pre">{`Direct Cost           = Medicare Reimbursement − Medicare CM/Case
Private Reimbursement = Medicare Reimbursement × Private-to-Medicare Ratio
Private CM/Case       = Private Reimbursement − Direct Cost
Private Margin %      = Private CM/Case / Private Reimbursement`}</pre>
        <p>
          Worked example, CABG isolated:
        </p>
        <pre className="procedure-db-formula-pre">{`Medicare reimbursement = $32.9k
Medicare CM/case       = $10.0k
Direct cost            = $32.9k − $10.0k = $22.9k
P/M ratio              = 2.01x
Private reimbursement  = $32.9k × 2.01   = $66.0k
Private CM/case        = $66.0k − $22.9k = $43.1k
Private margin %       = $43.1k / $66.0k = 65.3%`}</pre>

        <h3>Important limitations</h3>
        <ul className="procedure-db-limit-list">
          <li>Private payer reimbursement, LTV, and Halo are modeled benchmarks, not adjudicated contract rates. Hospitals should overwrite them with internal finance assumptions when those are available.</li>
          <li>The blended KPI tile on the ROI tab uses a flat 50/50 Medicare/Private mix. Per-customer payer-mix is a future refinement.</li>
          <li>Source horizon for Medicare reimbursement is FY 2025 IPPS / CY 2025–2026 OPPS / APC; rerun the deep-research benchmark periodically.</li>
        </ul>
      </section>

      <section className="procedure-db-methodology-deepdive">
        <h2>Full deep-research report</h2>
        {loading && <div className="admin-loading">Loading methodology…</div>}
        {loadError && <div className="procedure-db-error">Could not load methodology: {loadError}</div>}
        {!loading && !loadError && (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
          </div>
        )}
      </section>
    </div>
  );
}

function AddProcedureForm({ draft, onChange, onSave, onCancel }) {
  const set = (k) => (e) => onChange({ ...draft, [k]: e.target.value });
  return (
    <div className="procedure-db-add">
      <h3>Add New Procedure</h3>
      <p className="procedure-db-add-hint">
        Enter the basics here; full Medicare and Private economics can be
        filled in via the inline edit pencil after the row is created, or
        by re-importing a CSV.
      </p>
      <div className="grid-3">
        <input placeholder="Service Line *" value={draft.serviceLine} onChange={set('serviceLine')} />
        <input placeholder="Procedure Name *" value={draft.procedureName} onChange={set('procedureName')} />
        <input placeholder="Matching Key (comma-separated)" value={draft.matchingKey} onChange={set('matchingKey')} />
      </div>
      <div className="grid-4">
        <input placeholder="CPT Code(s)" value={draft.cptCodes} onChange={set('cptCodes')} />
        <input placeholder="MS-DRG/APC" value={draft.msDrgApc} onChange={set('msDrgApc')} />
        <input type="number" placeholder="Avg Medicare Reimb" value={draft.avgMedicareReimbursement} onChange={set('avgMedicareReimbursement')} />
        <input type="number" placeholder="Avg Private Reimb" value={draft.avgPrivateReimbursement} onChange={set('avgPrivateReimbursement')} />
      </div>
      <div className="procedure-db-add-actions">
        <button className="btn btn-green" onClick={onSave}>✓ Add</button>
        <button className="btn btn-grey" onClick={onCancel}>✕ Cancel</button>
      </div>
    </div>
  );
}

function EditRow({ draft, onChange, onSave, onCancel, canEdit }) {
  const set = (k, isNum = false) => (e) => onChange({
    ...draft,
    [k]: isNum ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value
  });
  const numVal = (v) => (v === null || v === undefined ? '' : v);
  return (
    <tr className="editing-row">
      <td><input value={draft.serviceLine || ''} onChange={set('serviceLine')} /></td>
      <td><input value={draft.procedureName || ''} onChange={set('procedureName')} /></td>
      <td><input value={draft.matchingKey || ''} onChange={set('matchingKey')} /></td>
      <td><input value={draft.cptCodes || ''} onChange={set('cptCodes')} /></td>
      <td><input value={draft.msDrgApc || ''} onChange={set('msDrgApc')} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" value={numVal(draft.avgMedicareReimbursement)} onChange={set('avgMedicareReimbursement', true)} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" step="0.01" value={numVal(draft.avgMedicareMarginPct)} onChange={set('avgMedicareMarginPct', true)} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" value={numVal(draft.avgMedicareProfitPerCase)} onChange={set('avgMedicareProfitPerCase', true)} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" value={numVal(draft.avgMedicareDirectLtv)} onChange={set('avgMedicareDirectLtv', true)} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" value={numVal(draft.avgMedicareHalo)} onChange={set('avgMedicareHalo', true)} /></td>
      <td className="procedure-db-cell-medicare"><input type="number" value={numVal(draft.avgMedicareTotalValue)} onChange={set('avgMedicareTotalValue', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" step="0.01" value={numVal(draft.privateToMedicareRatio)} onChange={set('privateToMedicareRatio', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" value={numVal(draft.avgPrivateReimbursement)} onChange={set('avgPrivateReimbursement', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" step="0.01" value={numVal(draft.avgPrivateMarginPct)} onChange={set('avgPrivateMarginPct', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" value={numVal(draft.avgPrivateProfitPerCase)} onChange={set('avgPrivateProfitPerCase', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" value={numVal(draft.avgPrivateDirectLtv)} onChange={set('avgPrivateDirectLtv', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" value={numVal(draft.avgPrivateHalo)} onChange={set('avgPrivateHalo', true)} /></td>
      <td className="procedure-db-cell-private"><input type="number" value={numVal(draft.avgPrivateTotalValue)} onChange={set('avgPrivateTotalValue', true)} /></td>
      {canEdit && (
        <td className="actions">
          <button className="icon-btn save" onClick={onSave} title="Save">✓</button>
          <button className="icon-btn cancel" onClick={onCancel} title="Cancel">✕</button>
        </td>
      )}
    </tr>
  );
}

function compareByServiceThenName(a, b) {
  const s = (a.serviceLine || '').localeCompare(b.serviceLine || '');
  if (s !== 0) return s;
  return (a.procedureName || '').localeCompare(b.procedureName || '');
}

function toApiPayload(draft) {
  return {
    serviceLine: (draft.serviceLine || '').trim(),
    procedureName: (draft.procedureName || '').trim(),
    matchingKey: (draft.matchingKey || '').trim() || null,
    cptCodes: (draft.cptCodes || '').trim() || null,
    msDrgApc: (draft.msDrgApc || '').trim() || null,

    avgMedicareReimbursement: numberOrNull(draft.avgMedicareReimbursement),
    avgMedicareMarginPct: numberOrNull(draft.avgMedicareMarginPct),
    avgMedicareProfitPerCase: numberOrNull(draft.avgMedicareProfitPerCase),
    avgMedicareDirectLtv: numberOrNull(draft.avgMedicareDirectLtv),
    avgMedicareHalo: numberOrNull(draft.avgMedicareHalo),
    avgMedicareTotalValue: numberOrNull(draft.avgMedicareTotalValue),
    medicareVolumeCategory: (draft.medicareVolumeCategory || '').trim() || null,

    privateToMedicareRatio: numberOrNull(draft.privateToMedicareRatio),
    avgPrivateReimbursement: numberOrNull(draft.avgPrivateReimbursement),
    avgPrivateMarginPct: numberOrNull(draft.avgPrivateMarginPct),
    avgPrivateProfitPerCase: numberOrNull(draft.avgPrivateProfitPerCase),
    avgPrivateDirectLtv: numberOrNull(draft.avgPrivateDirectLtv),
    avgPrivateHalo: numberOrNull(draft.avgPrivateHalo),
    avgPrivateTotalValue: numberOrNull(draft.avgPrivateTotalValue),
    privateVolumeCategory: (draft.privateVolumeCategory || '').trim() || null,

    dataBasisNotes: (draft.dataBasisNotes || '').trim() || null,
    sourceUrls: (draft.sourceUrls || '').trim() || null,
  };
}

export default ProcedureDatabase;
